import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import type { PairListItem } from "@pfh/shared";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { extractApiErrorMessage } from "@/lib/api";
import { openHedge } from "@/lib/api/manual-trade";
import { useConfirm } from "@/components/ui/dialog-imperative";
import {
  HedgeBracketPicker,
  type BracketResolved,
  type BracketState,
  type RunningPositionRef,
} from "@/components/trade/hedge-bracket-picker";
import { normalizeSymbol, parsePositive } from "@/components/trade/bracket-utils";
import type { PositionLive } from "@/store/dashboard.store";

interface Props {
  pair: PairListItem;
  renderTrigger: (open: () => void) => ReactNode;
  onSent?: () => void;
  /** Open positions on either leg's account. Picker offers a "Copy"
   *  button when one matches the symbol with a non-null SL/TP. */
  livePositions?: Record<string, PositionLive> | null;
}

function slotLabel(side: "long" | "short"): string {
  return side === "long" ? "Account A" : "Account B";
}

/**
 * Map a raw `abort_reason` from HedgePair into operator-readable copy.
 * The reason is wrapped as `<side>_open_failed: <code>` upstream, so
 * we match by substring rather than by exact equality. Falls back to
 * the raw string when no humanisation is registered.
 */
function humaniseAbortReason(raw: string): string {
  if (raw.includes("sl_crossed_market_during_stage")) {
    return "Stop loss crossed live market between submit and order placement. Widen the bracket and retry.";
  }
  if (raw.includes("tp_crossed_market_during_stage")) {
    return "Take profit crossed live market between submit and order placement. Widen the bracket and retry.";
  }
  return raw;
}

export function ManualHedgeOpenDialog({
  pair,
  renderTrigger,
  onSent,
  livePositions,
}: Props): ReactNode {
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [symbol, setSymbol] = useState(pair.symbol);
  const [lots, setLots] = useState(String(pair.lots));
  const [buyAccount, setBuyAccount] = useState(pair.longAccount.accountId);
  const [bracketState, setBracketState] = useState<BracketState>({
    stopLoss: "",
    takeProfit: "",
    mode: "price",
    perspective: "buy",
  });
  const [bracket, setBracket] = useState<BracketResolved>({
    ok: false,
    error: "Stop loss and take profit must be greater than zero.",
  });

  useEffect(() => {
    if (!open) return;
    setSymbol(pair.symbol);
    setLots(String(pair.lots));
    setBuyAccount(pair.longAccount.accountId);
    // Leave SL/TP empty on open. The picker auto-fills them at ±0.5%
    // once a live mid arrives, or the operator types / clicks a chip.
    setBracketState({ stopLoss: "", takeProfit: "", mode: "price", perspective: "buy" });
  }, [open, pair]);

  const sellAccount =
    buyAccount === pair.longAccount.accountId
      ? pair.shortAccount.accountId
      : pair.longAccount.accountId;
  const normalized = normalizeSymbol(symbol);
  const parsedLots = parsePositive(lots);

  const buyLegLabel = `${slotLabel(buyAccount === pair.longAccount.accountId ? "long" : "short")} · ${
    buyAccount === pair.longAccount.accountId ? pair.longAccount.firmName : pair.shortAccount.firmName
  }`;
  const sellLegLabel = `${slotLabel(sellAccount === pair.longAccount.accountId ? "long" : "short")} · ${
    sellAccount === pair.longAccount.accountId ? pair.longAccount.firmName : pair.shortAccount.firmName
  }`;

  const handleResolved = useCallback((b: BracketResolved): void => setBracket(b), []);

  const runningPositions = useMemo<RunningPositionRef[]>(() => {
    if (!livePositions) return [];
    const out: RunningPositionRef[] = [];
    for (const p of Object.values(livePositions)) {
      const isBuyLeg = p.accountId === buyAccount;
      const isSellLeg = p.accountId === sellAccount;
      if (!isBuyLeg && !isSellLeg) continue;
      out.push({
        accountId: p.accountId,
        legSide: isBuyLeg ? "buy" : "sell",
        positionSide: p.side,
        ticket: p.ticket,
        symbol: p.symbol,
        lots: p.lots,
        entry: p.entry,
        stopLoss: p.stopLoss,
        takeProfit: p.takeProfit,
        legLabel: isBuyLeg
          ? buyAccount === pair.longAccount.accountId
            ? pair.longAccount.firmName
            : pair.shortAccount.firmName
          : sellAccount === pair.longAccount.accountId
            ? pair.longAccount.firmName
            : pair.shortAccount.firmName,
      });
    }
    return out;
  }, [livePositions, buyAccount, sellAccount, pair.longAccount, pair.shortAccount]);

  const submit = async (): Promise<void> => {
    if (submitting) return;
    if (pair.status === "setup") {
      toast.error("Activate the pair before placing orders.");
      return;
    }
    if (!bracket.ok) {
      toast.error(bracket.error);
      return;
    }
    if (parsedLots == null) {
      toast.error("Lots is required.");
      return;
    }
    // Soft guard for full_auto: Haiku is autonomously deciding on this
    // pair right now. A manual hedge open can race the 3-stage
    // handshake. We allow it (operator override matters during news /
    // disputes) but force an explicit confirmation so it isn't an
    // accidental click.
    if (pair.mode === "full_auto") {
      const ok = await confirm({
        title: "AI Is Active On This Pair",
        description:
          "Haiku is autonomously deciding on this pair right now (full_auto). Your manual trade may conflict with autonomous decisions. Continue?",
        confirmText: "Open Anyway",
        destructive: true,
      });
      if (!ok) return;
    }
    setSubmitting(true);
    const startedAt = Date.now();
    try {
      const res = await openHedge({
        pairId: pair.id,
        symbol: normalized,
        lots: parsedLots,
        buyAccountId: buyAccount,
        sellAccountId: sellAccount,
        slDistancePrice: bracket.slDistancePrice,
        tpDistancePrice: bracket.tpDistancePrice,
      });
      if (res.ok) {
        toast.success(
          `Hedge opened — long #${res.longTicket ?? "?"} / short #${res.shortTicket ?? "?"}`,
        );
        setOpen(false);
        onSent?.();
        return;
      }
      const ms = Date.now() - startedAt;
      const detail =
        res.error === "timeout"
          ? `timeout after ${(ms / 1000).toFixed(1)}s · long=${res.longStatus ?? "?"} short=${res.shortStatus ?? "?"}`
          : (res.error
              ? humaniseAbortReason(res.error)
              : `aborted · long=${res.longStatus ?? "?"} short=${res.shortStatus ?? "?"}`);
      toast.error(`Hedge open failed — ${detail}`);
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {renderTrigger(() => setOpen(true))}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Open A New Hedge Trade</DialogTitle>
            <DialogDescription>
              The submitted lot size is only the ceiling. During prepare, both bots report live free margin and leverage, and the coordinator stages the smaller safe size before go.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2 sm:col-span-2">
                <label className="text-xs font-medium text-muted-foreground">Symbol</label>
                <Input
                  value={symbol}
                  onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                  placeholder="XAUUSD"
                />
              </div>
              <div className="grid gap-2">
                <label className="text-xs font-medium text-muted-foreground">Requested Lots</label>
                <Input
                  value={lots}
                  onChange={(e) => setLots(e.target.value)}
                  inputMode="decimal"
                />
              </div>
              <div className="grid gap-2">
                <label className="text-xs font-medium text-muted-foreground">Buy On</label>
                <select
                  value={buyAccount}
                  onChange={(e) => setBuyAccount(e.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value={pair.longAccount.accountId}>
                    {slotLabel("long")} · {pair.longAccount.firmName}
                  </option>
                  <option value={pair.shortAccount.accountId}>
                    {slotLabel("short")} · {pair.shortAccount.firmName}
                  </option>
                </select>
              </div>
            </div>

            <HedgeBracketPicker
              symbol={normalized}
              buyLegLabel={buyLegLabel}
              sellLegLabel={sellLegLabel}
              value={bracketState}
              onChange={setBracketState}
              onResolved={handleResolved}
              autoFillOnFirstQuote
              contractSize={pair.contractSize ?? null}
              runningPositions={runningPositions}
            />

            {pair.leverage != null ? (
              <div className="rounded-lg border border-border/60 bg-muted/20 p-3 text-xs text-muted-foreground">
                Pair leverage{" "}
                <span className="font-medium text-foreground">1:{pair.leverage}</span>.
                Both legs run prepare → stage → go before any order is placed; the smaller of the
                two safe-margin sizes wins.
              </div>
            ) : null}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submit}
              disabled={submitting || !bracket.ok}
            >
              {submitting ? "Opening Hedge…" : "Confirm Open"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
